import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { ChevronDown, User, Phone, MapPin, Calendar, FileText, Hash, Users, AlertCircle, Save, X, Mail } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

// 1. CORREÇÃO DO BUG DE FOCO: Componente de Wrapper movido para FORA da função principal
const InputWrapper = ({ icon: Icon, label, required, children, error }) => (
  <div className="group">
    <label className="block text-[11px] font-bold text-gray-500 uppercase tracking-wider mb-1.5 ml-1 transition-colors group-focus-within:text-[#ECAD21]">
      {label} {required && <span className="text-red-500 ml-0.5">*</span>}
    </label>
    <div className="relative">
      {Icon && (
        <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
          <Icon size={18} className={`transition-colors ${error ? 'text-red-400' : 'text-gray-400 group-focus-within:text-[#ECAD21]'}`} />
        </div>
      )}
      {children}
    </div>
  </div>
);

export default function AbaDadosPaciente({ onNovoPaciente, pacienteEditando, onFimEdicao, onDirtyChange }) {
  const { user } = useAuth();
  const isAluno = user?.role === 'aluno';

  const [numeroProntuario, setNumeroProntuario] = useState('');
  const [nome, setNome] = useState('');
  const [telefone, setTelefone] = useState('');
  const [mensagemErro, setMensagemErro] = useState('');
  
  const [formData, setFormData] = useState({
    tipo_paciente: 'NORMAL', numero_gaveta: '', rg: '', cpf: '', cep: '', data_nascimento: '',
    idade: '', endereco: '', numero: '', bairro: '', cidade: '', uf: '', observacao: '',
    responsavel_nome: '', responsavel_telefone: '', email: ''
  });

  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const [cepErro, setCepErro] = useState(false);

  useEffect(() => {
    if (pacienteEditando) {
      setNumeroProntuario(pacienteEditando.numero_prontuario || '');
      setNome(pacienteEditando.nome || '');
      setTelefone(formatarTelefone(pacienteEditando.telefone || ''));
      setFormData({
        tipo_paciente: pacienteEditando.tipo_paciente || 'NORMAL',
        numero_gaveta: pacienteEditando.numero_gaveta || '',
        rg: pacienteEditando.rg || '',
        cpf: pacienteEditando.cpf || '',
        cep: pacienteEditando.cep || '',
        data_nascimento: pacienteEditando.data_nascimento ? pacienteEditando.data_nascimento.slice(0, 10) : '',
        idade: pacienteEditando.idade || '',
        endereco: pacienteEditando.endereco || '',
        numero: pacienteEditando.numero || '',
        bairro: pacienteEditando.bairro || '',
        cidade: pacienteEditando.cidade || '',
        uf: pacienteEditando.uf || '',
        observacao: pacienteEditando.observacao || '',
        responsavel_nome: pacienteEditando.responsavel_nome || '',
        responsavel_telefone: formatarTelefone(pacienteEditando.responsavel_telefone || ''),
        email: pacienteEditando.email || ''
      });
      onDirtyChange?.(false);
    } else {
      resetForm();
      onDirtyChange?.(false);
    }
    setMensagemErro('');
  }, [pacienteEditando, onDirtyChange]);

  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) setDropdownOpen(false);
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  function resetForm() {
    setNumeroProntuario(''); setNome(''); setTelefone('');
    setFormData({
      tipo_paciente: 'NORMAL', numero_gaveta: '', rg: '', cpf: '', cep: '', data_nascimento: '',
      idade: '', endereco: '', numero: '', bairro: '', cidade: '', uf: '', observacao: '',
      responsavel_nome: '', responsavel_telefone: '', email: ''
    });
  }

  function formatarTelefone(campo) {
    const d = campo.replace(/\D/g, '');
    if (!d) return '';
    if (d.length <= 2) return d.replace(/^(\d{0,2})/, '($1');
    if (d.length <= 6) return d.replace(/^(\d{2})(\d{0,4})/, '($1) $2');
    return d.replace(/^(\d{2})(\d{5})(\d{0,4}).*/, '($1) $2-$3');
  }

  function calcularIdade(dataStr) {
    const hoje = new Date();
    const nasc = new Date(dataStr);
    let idade = hoje.getFullYear() - nasc.getFullYear();
    if (hoje.getMonth() < nasc.getMonth() || (hoje.getMonth() === nasc.getMonth() && hoje.getDate() < nasc.getDate())) idade--;
    return idade;
  }

  function validarCPF(cpf) {
    const str = cpf.replace(/\D/g, '');
    if (str.length !== 11 || /^(\d)\1+$/.test(str)) return false;
    const calc = pos => {
      let sum = 0;
      for (let i = 0; i < pos; i++) sum += parseInt(str.charAt(i)) * (pos + 1 - i);
      let res = (sum * 10) % 11;
      return res === 10 ? 0 : res;
    };
    return calc(9) === parseInt(str.charAt(9)) && calc(10) === parseInt(str.charAt(10));
  }

  function validarNomeCompleto(valor) {
    const partes = valor.trim().split(/\s+/);
    return partes.length >= 2 && partes.every(p => p.length >= 2);
  }

  async function handleCepBlur() {
    const cepLimpo = formData.cep.replace(/\D/g, '');
    if (cepLimpo.length !== 8) return;
    setCepErro(false);
    try {
      const { data } = await axios.get(`https://viacep.com.br/ws/${cepLimpo}/json/`);
      if (!data.erro) {
        let novaObservacao = formData.observacao;
        if (data.complemento && data.complemento.trim()) {
          const complementoTexto = `Complemento: ${data.complemento}`;
          if (!formData.observacao.includes(complementoTexto)) {
            novaObservacao = formData.observacao ? `${formData.observacao}\n${complementoTexto}` : complementoTexto;
          }
        }
        setFormData(fd => ({
          ...fd, endereco: data.logradouro || '', bairro: data.bairro || '', cidade: data.localidade || '', uf: data.uf || '', observacao: novaObservacao
        }));
        if (data.logradouro) toast.success('Endereço localizado!', { autoClose: 2000, icon: '📍' });
      } else {
        setCepErro(true); toast.error('CEP não encontrado.');
      }
    } catch {
      setCepErro(true); toast.error('Erro ao buscar CEP.');
    }
  }

  const handleChange = e => {
    const { name, value } = e.target;
    setFormData(fd => ({ ...fd, [name]: value }));
    onDirtyChange?.(true);
    if (name === 'cep' && cepErro) setCepErro(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && e.target.tagName !== 'TEXTAREA') {
      e.preventDefault();
      const form = e.target.form;
      const formElements = Array.from(form.elements).filter(el => !el.disabled && el.type !== 'hidden' && el.tabIndex >= 0);
      const currentIndex = formElements.indexOf(e.target);
      if (formElements[currentIndex + 1]) formElements[currentIndex + 1].focus();
    }
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setMensagemErro('');

    if (!validarNomeCompleto(nome)) { setMensagemErro('Digite o nome completo (nome e sobrenome).'); return; }

    const tipo = (formData.tipo_paciente || 'NORMAL').toUpperCase();
    const somenteDigitos = telefone.replace(/\D/g, '');
    const respDigitos = (formData.responsavel_telefone || '').replace(/\D/g, '');

    if (tipo === 'NORMAL' && somenteDigitos.length !== 10 && somenteDigitos.length !== 11) {
      setMensagemErro('Telefone obrigatório (10 ou 11 dígitos) para paciente NORMAL.'); return;
    }
    if (tipo !== 'NORMAL') {
      if (somenteDigitos && somenteDigitos.length !== 10 && somenteDigitos.length !== 11) { setMensagemErro('Telefone inválido.'); return; }
      if (respDigitos && respDigitos.length !== 10 && respDigitos.length !== 11) { setMensagemErro('Telefone do responsável inválido.'); return; }
    }

    try {
      if (somenteDigitos) {
        const { data } = await axios.get(`/api/pacientes?telefone=${somenteDigitos}`);
        const existeOutro = Array.isArray(data) && data.some(p => String(p.id) !== String(pacienteEditando?.id));
        if (existeOutro) { setMensagemErro('Já existe um paciente cadastrado com este telefone.'); return; }
      }
    } catch (err) {}

    // VALIDAÇÃO DE CPF OBRIGATÓRIO
    if (!isAluno) {
      if (!formData.cpf || formData.cpf.trim() === '') {
        setMensagemErro('O CPF é obrigatório.');
        return;
      }
      if (!validarCPF(formData.cpf)) {
        setMensagemErro('CPF inválido.');
        return;
      }
    }

    const dados = {
      numero_prontuario: isAluno ? null : (numeroProntuario.trim() || null),
      nome: nome.trim(), telefone: somenteDigitos || null,
      numero_gaveta: isAluno ? null : (formData.numero_gaveta || null),
      rg: isAluno ? null : (formData.rg || null), cpf: isAluno ? null : (formData.cpf || null),
      cep: isAluno ? null : (formData.cep || null), data_nascimento: isAluno ? null : (formData.data_nascimento || null),
      idade: isAluno ? null : (formData.idade || null), endereco: isAluno ? null : (formData.endereco || null),
      numero: isAluno ? null : (formData.numero || null), bairro: isAluno ? null : (formData.bairro || null),
      cidade: formData.cidade || null, uf: isAluno ? null : (formData.uf || null),
      observacao: isAluno ? null : (formData.observacao || null), tipo_paciente: formData.tipo_paciente || 'NORMAL',
      responsavel_nome: formData.responsavel_nome || null, responsavel_telefone: respDigitos || null,
      email: formData.email ? formData.email.trim() : null
    };

    try {
      if (pacienteEditando) {
        await axios.put(`/api/pacientes/${pacienteEditando.id}`, dados);
        toast.success('Paciente atualizado com sucesso!');
        setTimeout(() => onNovoPaciente(), 500);
      } else {
        await axios.post('/api/pacientes', dados);
        toast.success('Paciente cadastrado com sucesso!');
        resetForm(); setNome(''); setTelefone(''); setNumeroProntuario('');
        setTimeout(() => onNovoPaciente(), 500);
      }
    } catch (err) {
      setMensagemErro(err.response?.data?.error || err.response?.data || err.message || 'Erro ao salvar paciente.');
    }
  };

  return (
    <form onSubmit={handleSubmit} autoComplete="off" onKeyDown={handleKeyDown} className="space-y-6">
      
      {/* CARD 1: IDENTIFICAÇÃO PRINCIPAL */}
      <div className="bg-white border border-gray-100 shadow-sm rounded-2xl p-5 md:p-6">
        <div className="flex flex-col md:flex-row items-center md:items-start gap-6">
          
          <div className="shrink-0 flex flex-col items-center">
            <img src={pacienteEditando?.avatar_url || `https://ui-avatars.com/api/?name=${encodeURIComponent(nome || 'Paciente')}&background=0095DA&color=fff&size=96`} alt={nome} className="w-24 h-24 rounded-2xl object-cover shadow-sm border border-gray-100" />
            <div className="mt-3 bg-blue-50 text-[#0095DA] px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wide">
              {pacienteEditando ? 'Edição' : 'Novo Cadastro'}
            </div>
          </div>

          <div className="flex-1 w-full space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <InputWrapper icon={User} label="Nome Completo" required>
                <input type="text" required value={nome} onChange={e => { setNome(e.target.value); onDirtyChange?.(true); }} className="w-full pl-10 pr-4 py-2.5 bg-gray-50 hover:bg-white border border-gray-200 focus:border-[#ECAD21] rounded-xl focus:outline-none focus:ring-4 focus:ring-yellow-50 transition-all text-sm font-medium text-gray-800" />
              </InputWrapper>
              
              <div className="group">
                <label className="block text-[11px] font-bold text-gray-500 uppercase tracking-wider mb-1.5 ml-1 transition-colors group-focus-within:text-[#ECAD21]">Classificação</label>
                <div className="relative" ref={dropdownRef}>
                  <button type="button" onClick={() => setDropdownOpen(!dropdownOpen)} className="w-full pl-4 pr-4 py-2.5 bg-gray-50 hover:bg-white border border-gray-200 focus:border-[#ECAD21] rounded-xl flex items-center justify-between focus:outline-none focus:ring-4 focus:ring-yellow-50 transition-all">
                    <div className="flex items-center gap-2.5">
                      <span className={`w-2.5 h-2.5 rounded-full ${formData.tipo_paciente === 'NORMAL' ? 'bg-blue-500 shadow-[0_0_8px_rgba(59,130,246,0.5)]' : formData.tipo_paciente === 'PEDIATRICO' ? 'bg-yellow-400 shadow-[0_0_8px_rgba(250,204,21,0.5)]' : 'bg-green-500 shadow-[0_0_8px_rgba(34,197,94,0.5)]'}`} />
                      <span className="text-sm font-medium text-gray-800">{formData.tipo_paciente === 'NORMAL' ? 'Paciente Normal' : formData.tipo_paciente === 'PEDIATRICO' ? 'Odontopediatria' : 'Odontogeriatria'}</span>
                    </div>
                    <ChevronDown size={18} className={`text-gray-400 transition-transform duration-300 ${dropdownOpen ? 'rotate-180' : ''}`} />
                  </button>
                  {dropdownOpen && (
                    <div className="absolute z-50 w-full mt-2 bg-white border border-gray-100 rounded-xl shadow-xl overflow-hidden py-1">
                      {['NORMAL', 'PEDIATRICO', 'GERIATRICO'].map(tipo => (
                        <button key={tipo} type="button" onClick={() => { setFormData(fd => ({ ...fd, tipo_paciente: tipo })); setDropdownOpen(false); onDirtyChange?.(true); }} className="w-full px-4 py-2.5 flex items-center gap-3 hover:bg-gray-50 transition-colors text-left text-sm font-medium text-gray-700">
                          <span className={`w-2 h-2 rounded-full ${tipo === 'NORMAL' ? 'bg-blue-500' : tipo === 'PEDIATRICO' ? 'bg-yellow-400' : 'bg-green-500'}`} />
                          {tipo === 'NORMAL' ? 'Paciente Normal' : tipo === 'PEDIATRICO' ? 'Odontopediatria' : 'Odontogeriatria'}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {!isAluno && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <InputWrapper icon={Hash} label="Prontuário Físico">
                  <input type="text" maxLength={8} value={numeroProntuario} onChange={e => { setNumeroProntuario(e.target.value); onDirtyChange?.(true); }} className="w-full pl-10 pr-4 py-2.5 bg-gray-50 hover:bg-white border border-gray-200 focus:border-[#ECAD21] rounded-xl focus:outline-none focus:ring-4 focus:ring-yellow-50 transition-all text-sm font-medium text-gray-800" placeholder="00000000" />
                </InputWrapper>
                <InputWrapper icon={FileText} label="Gaveta / Arquivo">
                  <input type="text" name="numero_gaveta" value={formData.numero_gaveta} onChange={handleChange} className="w-full pl-10 pr-4 py-2.5 bg-gray-50 hover:bg-white border border-gray-200 focus:border-[#ECAD21] rounded-xl focus:outline-none focus:ring-4 focus:ring-yellow-50 transition-all text-sm font-medium text-gray-800" placeholder="Localização física" />
                </InputWrapper>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* CARD 2: CONTATO E RESPONSÁVEL */}
      <div className="bg-white border border-gray-100 shadow-sm rounded-2xl p-5 md:p-6 space-y-4">
        <h3 className="text-sm font-bold text-gray-800 flex items-center gap-2 mb-2 border-b border-gray-100 pb-3">
          <Phone size={18} className="text-[#ECAD21]" /> Meios de Contato
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <InputWrapper icon={Phone} label="Telefone / WhatsApp" required={formData.tipo_paciente === 'NORMAL'}>
            <input type="tel" required={formData.tipo_paciente === 'NORMAL'} value={telefone} onChange={e => { setTelefone(formatarTelefone(e.target.value)); onDirtyChange?.(true); }} placeholder="(11) 99999-9999" className="w-full pl-10 pr-4 py-2.5 bg-gray-50 hover:bg-white border border-gray-200 focus:border-[#ECAD21] rounded-xl focus:outline-none focus:ring-4 focus:ring-yellow-50 transition-all text-sm font-medium text-gray-800" />
          </InputWrapper>
          <InputWrapper icon={Mail} label="E-mail">
            <input type="email" name="email" value={formData.email} onChange={handleChange} placeholder="paciente@email.com" className="w-full pl-10 pr-4 py-2.5 bg-gray-50 hover:bg-white border border-gray-200 focus:border-[#ECAD21] rounded-xl focus:outline-none focus:ring-4 focus:ring-yellow-50 transition-all text-sm font-medium text-gray-800" />
          </InputWrapper>
        </div>

        {formData.tipo_paciente !== 'NORMAL' && (
          <div className="mt-4 bg-orange-50/50 border border-orange-100 rounded-xl p-4">
            <div className="flex items-center gap-2 text-orange-800 font-semibold text-sm mb-3">
              <Users size={16} /> Dados do Responsável Legal
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <InputWrapper icon={User} label="Nome do Responsável">
                <input type="text" name="responsavel_nome" value={formData.responsavel_nome} onChange={handleChange} className="w-full pl-10 pr-4 py-2.5 bg-white border border-orange-200 focus:border-orange-400 rounded-xl focus:outline-none focus:ring-4 focus:ring-orange-50 transition-all text-sm font-medium text-gray-800" />
              </InputWrapper>
              <InputWrapper icon={Phone} label="Telefone do Responsável">
                <input type="tel" name="responsavel_telefone" value={formData.responsavel_telefone} onChange={e => setFormData(fd => ({ ...fd, responsavel_telefone: formatarTelefone(e.target.value) }))} placeholder="(XX) 99999-9999" className="w-full pl-10 pr-4 py-2.5 bg-white border border-orange-200 focus:border-orange-400 rounded-xl focus:outline-none focus:ring-4 focus:ring-orange-50 transition-all text-sm font-medium text-gray-800" />
              </InputWrapper>
            </div>
          </div>
        )}
      </div>

      {/* CARD 3: DOCUMENTAÇÃO E NASCIMENTO */}
      {!isAluno && (
        <div className="bg-white border border-gray-100 shadow-sm rounded-2xl p-5 md:p-6 space-y-4">
          <h3 className="text-sm font-bold text-gray-800 flex items-center gap-2 mb-2 border-b border-gray-100 pb-3">
            <FileText size={18} className="text-[#ECAD21]" /> Documentação e Perfil
          </h3>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
            <InputWrapper icon={FileText} label="RG">
              <input type="text" name="rg" value={formData.rg} onChange={handleChange} className="w-full pl-10 pr-4 py-2.5 bg-gray-50 hover:bg-white border border-gray-200 focus:border-[#ECAD21] rounded-xl focus:outline-none focus:ring-4 focus:ring-yellow-50 transition-all text-sm font-medium text-gray-800" placeholder="00.000.000-0" />
            </InputWrapper>
            <InputWrapper icon={FileText} label="CPF" required>
              <input type="text" name="cpf" required value={formData.cpf} onChange={handleChange} placeholder="000.000.000-00" className="w-full pl-10 pr-4 py-2.5 bg-gray-50 hover:bg-white border border-gray-200 focus:border-[#ECAD21] rounded-xl focus:outline-none focus:ring-4 focus:ring-yellow-50 transition-all text-sm font-medium text-gray-800" />
            </InputWrapper>
            <InputWrapper icon={Calendar} label="Data Nascimento">
              <input type="date" name="data_nascimento" value={formData.data_nascimento} onChange={e => { const val = e.target.value; setFormData(fd => ({ ...fd, data_nascimento: val, idade: val ? calcularIdade(val) : '' })); }} className="w-full pl-10 pr-4 py-2.5 bg-gray-50 hover:bg-white border border-gray-200 focus:border-[#ECAD21] rounded-xl focus:outline-none focus:ring-4 focus:ring-yellow-50 transition-all text-sm font-medium text-gray-800" />
            </InputWrapper>
            <InputWrapper label="Idade Atual">
              <input type="text" readOnly value={formData.idade ? `${formData.idade} anos` : ''} className="w-full px-4 py-2.5 bg-gray-100 border border-gray-200 rounded-xl focus:outline-none text-sm font-bold text-gray-500 cursor-not-allowed text-center" placeholder="-" />
            </InputWrapper>
          </div>
        </div>
      )}

      {/* CARD 4: ENDEREÇO E OBSERVAÇÕES */}
      <div className="bg-white border border-gray-100 shadow-sm rounded-2xl p-5 md:p-6 space-y-4">
        <h3 className="text-sm font-bold text-gray-800 flex items-center gap-2 mb-2 border-b border-gray-100 pb-3">
          <MapPin size={18} className="text-[#ECAD21]" /> Endereço Residencial
        </h3>

        {isAluno ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <InputWrapper icon={MapPin} label="Cidade (Opcional)">
              <input type="text" name="cidade" value={formData.cidade} onChange={handleChange} className="w-full pl-10 pr-4 py-2.5 bg-gray-50 hover:bg-white border border-gray-200 focus:border-[#ECAD21] rounded-xl focus:outline-none focus:ring-4 focus:ring-yellow-50 transition-all text-sm font-medium text-gray-800" />
            </InputWrapper>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
              <div className="md:col-span-2">
                <InputWrapper icon={MapPin} label="CEP" error={cepErro}>
                  <input type="text" name="cep" value={formData.cep} onChange={handleChange} onBlur={handleCepBlur} placeholder="00000-000" className={`w-full pl-10 pr-4 py-2.5 border rounded-xl focus:outline-none transition-all text-sm font-medium ${cepErro ? 'bg-red-50 border-red-300 focus:border-red-500 focus:ring-4 focus:ring-red-100 text-red-900' : 'bg-gray-50 hover:bg-white border-gray-200 focus:border-[#ECAD21] focus:ring-4 focus:ring-yellow-50 text-gray-800'}`} />
                </InputWrapper>
              </div>
              <div className="md:col-span-3">
                <InputWrapper label="Logradouro (Rua, Av)">
                  <input type="text" name="endereco" value={formData.endereco} onChange={handleChange} className="w-full px-4 py-2.5 bg-gray-50 hover:bg-white border border-gray-200 focus:border-[#ECAD21] rounded-xl focus:outline-none focus:ring-4 focus:ring-yellow-50 transition-all text-sm font-medium text-gray-800" />
                </InputWrapper>
              </div>
              <div className="md:col-span-1">
                <InputWrapper label="Número">
                  <input type="text" name="numero" value={formData.numero} onChange={handleChange} className="w-full px-4 py-2.5 bg-gray-50 hover:bg-white border border-gray-200 focus:border-[#ECAD21] rounded-xl focus:outline-none focus:ring-4 focus:ring-yellow-50 transition-all text-sm font-medium text-gray-800" />
                </InputWrapper>
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
              <div className="md:col-span-2">
                <InputWrapper label="Bairro">
                  <input type="text" name="bairro" value={formData.bairro} onChange={handleChange} className="w-full px-4 py-2.5 bg-gray-50 hover:bg-white border border-gray-200 focus:border-[#ECAD21] rounded-xl focus:outline-none focus:ring-4 focus:ring-yellow-50 transition-all text-sm font-medium text-gray-800" />
                </InputWrapper>
              </div>
              <div className="md:col-span-3">
                <InputWrapper label="Cidade">
                  <input type="text" name="cidade" value={formData.cidade} onChange={handleChange} className="w-full px-4 py-2.5 bg-gray-50 hover:bg-white border border-gray-200 focus:border-[#ECAD21] rounded-xl focus:outline-none focus:ring-4 focus:ring-yellow-50 transition-all text-sm font-medium text-gray-800" />
                </InputWrapper>
              </div>
              <div className="md:col-span-1">
                <InputWrapper label="UF">
                  <input type="text" name="uf" value={formData.uf} onChange={handleChange} maxLength={2} placeholder="SP" className="w-full px-4 py-2.5 bg-gray-50 hover:bg-white border border-gray-200 focus:border-[#ECAD21] rounded-xl focus:outline-none focus:ring-4 focus:ring-yellow-50 uppercase transition-all text-sm font-medium text-gray-800 text-center" />
                </InputWrapper>
              </div>
            </div>

            <div className="pt-2">
              <InputWrapper label="Observações Adicionais / Complemento">
                <textarea name="observacao" value={formData.observacao} onChange={handleChange} className="w-full px-4 py-3 bg-gray-50 hover:bg-white border border-gray-200 focus:border-[#ECAD21] rounded-xl focus:outline-none focus:ring-4 focus:ring-yellow-50 transition-all text-sm font-medium text-gray-800 min-h-[100px] custom-scrollbar" placeholder="Instruções de acesso, complemento de endereço, condições específicas..." />
              </InputWrapper>
            </div>
          </>
        )}
      </div>

      {/* FEEDBACK DE ERRO */}
      {mensagemErro && (
        <div className="flex items-center gap-3 p-4 bg-red-50 border border-red-200 text-red-700 rounded-xl shadow-sm animate-fade-in">
          <AlertCircle size={20} className="shrink-0" />
          <p className="text-sm font-bold">{mensagemErro}</p>
        </div>
      )}

      {/* 2. CORREÇÃO DO LAYOUT: Rodapé com espaçamento extra embaixo (pb-8 e mb-4) para desgrudar do fim da tela */}
      <div className="mt-8 pt-6 pb-8 mb-4 border-t border-gray-100 flex justify-end gap-3">
        {pacienteEditando && (
          <button type="button" onClick={onFimEdicao} className="flex items-center gap-2 bg-gray-100 hover:bg-gray-200 text-gray-700 font-bold px-6 py-3 rounded-xl transition-all text-sm shadow-sm">
            <X size={18} /> Cancelar
          </button>
        )}
        <button type="submit" className="flex items-center gap-2 bg-[#0095DA] hover:bg-blue-600 text-white font-bold px-8 py-3 rounded-xl transition-all shadow-md hover:shadow-lg hover:-translate-y-0.5 text-sm">
          <Save size={18} />
          {pacienteEditando ? 'Salvar Alterações' : 'Cadastrar Paciente'}
        </button>
      </div>

    </form>
  );
}